import React, { useState, useEffect, useContext } from 'react';
import { Card, Form, InputNumber, Select, Input, Button, Table, Tag, Space, Popconfirm, Empty, Modal, Alert } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import EUserTypes from '../../enums/EUserTypes';
import EFluidType from '../../enums/EFluidType';
import { ScreenHeader, QuantityStep, FLOW_COLORS, primaryButtonStyle, BackToHome } from '../../components/drum/WizardParts';
import MachineSelector from '../../components/machine/MachineSelector';
import FluidCardSelect from '../../components/fluid/FluidCardSelect';
import JustificationModal, { JustificationInput, isJustificationValid } from '../../components/modals/JustificationModal';

/**
 * Painel de Descarga: registra as descargas da máquina ao longo do dia.
 * - Cada descarga especifica o tipo de fluido (solvente, borra, água, solvente amarelo) e litros.
 * - Toda alteração gera AuditLog no back-end.
 * O total descarregado por tipo alimenta o "Estoque Descarregado" (estoque real).
 *
 * Leitura do tanque obrigatória: toda descarga começa com o operador informando
 * quanto o tanque marca naquele momento — SEMPRE o valor ANTES da descarga.
 * Esse valor vira uma leitura de tanque ancorada à descarga (criada pela API na
 * mesma transação) e passa pelas mesmas salvaguardas do menu Tanque: modal de
 * confirmação com a variação inferida e redigitação quando a leitura é suspeita.
 *
 * Correções (regra do cliente):
 * - Admin edita/exclui qualquer registro (justificativa opcional).
 * - Operador-comprador e Líder editam/excluem registros DE HOJE (de qualquer
 *   operador), sempre com justificativa obrigatória gravada na auditoria.
 * - Operador comum só inclui e visualiza.
 *
 * Prop `mobile` (opcional): quando true (operadores no PWA), UI vertical, inputs full-width,
 * lista **apenas do dia atual** como cards compactos. Quando não passada, deriva de `!isAdmin`.
 */
const DrenagemPanel = ({ mobile: mobileProp }) => {
  const { loggedUser } = useContext(GeneralContext);
  const location = useLocation();
  const isAdmin = loggedUser?.type === EUserTypes.PANEL_ADMIN.code;
  const mobile = mobileProp !== undefined ? mobileProp : !isAdmin;
  const flowColor = location.state?.color || FLOW_COLORS.drenagem;
  // Comprador/líder (e admin) podem corrigir registros de hoje pelo PWA.
  const canCorrect = EUserTypes.canEditToday(loggedUser);
  // Justificativa obrigatória para não-admin (validada também na API).
  const justRequired = !isAdmin;

  const [form] = Form.useForm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  // Correção no PWA: modal de edição (campos + justificativa) e alvo de exclusão.
  const [editModal, setEditModal] = useState(null); // registro sendo editado
  const [editJustification, setEditJustification] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // registro a excluir
  const [deleting, setDeleting] = useState(false);
  // Máquina da sessão atual (em memória). Escolhida ao abrir o painel; fica
  // selecionada enquanto o operador permanece na tela. Re-iniciar o fluxo
  // (voltar à Home e abrir de novo) exige escolher a máquina de novo.
  const [machine, setMachine] = useState(null);
  // Lista de máquinas para o filtro do admin.
  const [machines, setMachines] = useState([]);
  const [machineFilter, setMachineFilter] = useState(undefined);
  // Confirmação da leitura do tanque (valor ANTES da descarga): mesmo padrão
  // do TanquePanel — preview com a variação inferida e, em leitura suspeita,
  // redigitação obrigatória do valor.
  const [confirmState, setConfirmState] = useState(null); // { values, preview }
  const [retypeValue, setRetypeValue] = useState(null);

  const isToday = (dateStr) => {
    const d = new Date(dateStr);
    const n = new Date();
    return d.toDateString() === n.toDateString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (machineFilter) params.machineId = machineFilter;
      const res = await api.operation.listDrainages(params);
      const l = res.data?.rows || res.data || [];
      setList(Array.isArray(l) ? l : []);
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao carregar descargas.');
    } finally {
      setLoading(false);
    }
  };

  const loadMachines = async () => {
    try {
      const res = await api.machine.list({ page: 1, pageSize: 100 });
      setMachines(res.data?.rows || []);
    } catch (e) { /* silencioso */ }
  };

  useEffect(() => { load(); loadMachines(); }, []);
  useEffect(() => { if (!mobile) load(); }, [machineFilter]);

  /**
   * Passo 1 do registro: calcula o preview da leitura do tanque (valor ANTES
   * da descarga) e abre o modal de confirmação. Nada é gravado aqui.
   */
  const handleAdd = async (values) => {
    if (!machine?.id) {
      showErrorMessage('Selecione a máquina onde a descarga foi feita.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.operation.previewTankReading({ machine_id: machine.id, liters: values.tank_liters });
      setRetypeValue(null);
      setConfirmState({ values, preview: res.data });
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao calcular a leitura do tanque.');
    } finally {
      setSaving(false);
    }
  };

  /** Passo 2: confirmação do operador no modal — grava descarga + leitura. */
  const handleConfirmSave = async () => {
    if (!confirmState) return;
    setSaving(true);
    try {
      await api.operation.createDrainage({ ...confirmState.values, machine_id: machine.id });
      showSuccessMessage('Descarga registrada!');
      setConfirmState(null);
      form.resetFields();
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao registrar descarga.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    try {
      await api.operation.updateDrainage(id, editRecord);
      showSuccessMessage('Descarga atualizada!');
      setEditingId(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao atualizar descarga.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.operation.deleteDrainage(id);
      showSuccessMessage('Descarga removida!');
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao remover descarga.');
    }
  };

  /** Salva a edição feita no modal do PWA (campos + justificativa). */
  const handleMobileUpdate = async () => {
    if (!editModal) return;
    setSavingEdit(true);
    try {
      await api.operation.updateDrainage(editModal.id, { ...editRecord, justification: editJustification.trim() || undefined });
      showSuccessMessage('Descarga corrigida!');
      setEditModal(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao corrigir descarga.');
    } finally {
      setSavingEdit(false);
    }
  };

  /** Exclui via modal de justificativa (PWA). */
  const handleMobileDelete = async (justification) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.operation.deleteDrainage(deleteTarget.id, { justification: justification || undefined });
      showSuccessMessage('Descarga removida!');
      setDeleteTarget(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao remover descarga.');
    } finally {
      setDeleting(false);
    }
  };

  /** Abre o modal de correção com os valores atuais do registro. */
  const openEditModal = (r) => {
    setEditRecord({ fluid_type: r.fluid_type, liters: Number(r.liters), observation: r.observation, machine_id: r.machine_id });
    setEditJustification('');
    setEditModal(r);
  };

  const columns = [
    { title: 'Data/hora', dataIndex: 'occurred_at', key: 'occurred_at', render: v => new Date(v).toLocaleString('pt-BR') },
    {
      title: 'Fluido', dataIndex: 'fluid_type', key: 'fluid_type',
      render: v => { const f = EFluidType.getByCode(v); return f ? <Tag>{f.description}</Tag> : v; }
    },
    {
      title: 'Litros', key: 'liters',
      render: (_, r) => editingId === r.id ? (
        <InputNumber value={editRecord?.liters} onChange={v => setEditRecord(p => ({ ...p, liters: v }))} min={0} step={0.1} decimalSeparator="," />
      ) : r.liters
    },
    {
      title: 'Máquina', key: 'machine',
      render: (_, r) => editingId === r.id ? (
        <Select
          value={editRecord?.machine_id}
          onChange={v => setEditRecord(p => ({ ...p, machine_id: v }))}
          options={machines.map(m => ({ value: m.id, label: m.code }))}
          style={{ width: 140 }}
          placeholder="Máquina"
        />
      ) : (r.machine ? `${r.machine.code}` : '—')
    },
    {
      title: 'Obs.', key: 'observation',
      render: (_, r) => editingId === r.id ? (
        <Input value={editRecord?.observation || ''} onChange={e => setEditRecord(p => ({ ...p, observation: e.target.value }))} />
      ) : (r.observation || '—')
    },
    { title: 'Usuário', key: 'user', render: (_, r) => r.user?.full_name || '—' },
    {
      title: 'Ações', key: 'actions',
      render: (_, r) => {
        // No PWA (mobile) o operador não edita/exclui — só inclusão e visualização.
        const canEdit = mobile ? isAdmin : (isAdmin || isToday(r.occurred_at));
        if (!canEdit) return null;
        if (editingId === r.id) {
          return (
            <Space>
              <Button size="small" type="primary" icon={<SaveOutlined />} onClick={() => handleUpdate(r.id)} />
              <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingId(null)} />
            </Space>
          );
        }
        return (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); setEditRecord({ liters: r.liters, fluid_type: r.fluid_type, observation: r.observation, occurred_at: r.occurred_at, machine_id: r.machine_id }); }}>Editar</Button>
            <Popconfirm
              title="Excluir esta descarga?"
              okText="Excluir"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(r.id)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  const todaysList = list.filter((r) => isToday(r.occurred_at));

  // Modal de correção do PWA (comprador/líder/admin): campos + justificativa.
  const mobileEditModal = (
    <Modal
      title="Corrigir descarga"
      open={!!editModal}
      onCancel={() => setEditModal(null)}
      centered
      footer={[
        <Button key="cancel" onClick={() => setEditModal(null)}>Cancelar</Button>,
        <Button
          key="ok"
          type="primary"
          loading={savingEdit}
          disabled={!isJustificationValid(editJustification, justRequired)}
          onClick={handleMobileUpdate}
        >
          Salvar correção
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Fluido</div>
          {/* Mesmo seletor do formulário de registro (consistência no PWA). */}
          <FluidCardSelect
            value={editRecord?.fluid_type}
            onChange={(v) => setEditRecord((p) => ({ ...p, fluid_type: v }))}
            color={flowColor}
          />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Litros</div>
          <InputNumber
            value={editRecord?.liters}
            onChange={(v) => setEditRecord((p) => ({ ...p, liters: v }))}
            min={0}
            step={0.1}
            decimalSeparator=","
            style={{ width: '100%' }}
            size="large"
            aria-label="Litros da descarga"
          />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Máquina</div>
          <Select
            value={editRecord?.machine_id}
            onChange={(v) => setEditRecord((p) => ({ ...p, machine_id: v }))}
            options={machines.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
            style={{ width: '100%' }}
            size="large"
            aria-label="Máquina da descarga"
          />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Observação</div>
          <Input.TextArea
            value={editRecord?.observation || ''}
            onChange={(e) => setEditRecord((p) => ({ ...p, observation: e.target.value }))}
            autoSize={{ minRows: 2 }}
            aria-label="Observação da descarga"
          />
        </div>
        <JustificationInput value={editJustification} onChange={setEditJustification} required={justRequired} />
      </div>
    </Modal>
  );

  const fmtL = (v) => `${Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} L`;

  const preview = confirmState?.preview;
  const informedTank = confirmState?.values?.tank_liters;
  const confirmFluid = confirmState ? EFluidType.getByCode(confirmState.values.fluid_type) : null;
  // Leitura suspeita: menor que o esperado (variação negativa) ou transferindo
  // mais do que a máquina tem registrado (implausível) — exige redigitação.
  const suspicious = preview ? (preview.variation < 0 || preview.implausible) : false;
  const retypeMatches = retypeValue != null && Number(retypeValue) === Number(informedTank);

  // Modal de confirmação da descarga + leitura do tanque (PWA e desktop).
  const confirmModal = (
    <Modal
      title={suspicious ? (<span><WarningOutlined style={{ color: '#d97706', marginRight: 8 }} />Leitura suspeita — confira o valor</span>) : 'Confirmar descarga'}
      open={!!confirmState}
      onCancel={() => setConfirmState(null)}
      centered
      footer={[
        <Button key="cancel" onClick={() => setConfirmState(null)}>Cancelar</Button>,
        <Button
          key="ok"
          type="primary"
          danger={suspicious}
          loading={saving}
          disabled={suspicious && !retypeMatches}
          onClick={handleConfirmSave}
        >
          {suspicious ? 'Confirmar mesmo assim' : 'Confirmar descarga'}
        </Button>,
      ]}
    >
      {preview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Tanque informado ANTES da descarga</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: flowColor }}>{fmtL(informedTank)}</div>
            <div style={{ fontSize: 14, color: '#374151', marginTop: 4 }}>
              Descarga: <strong>{fmtL(confirmState.values.liters)}</strong> de <strong>{confirmFluid ? confirmFluid.description : confirmState.values.fluid_type}</strong>
            </div>
          </div>

          <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 14 }}>
            <div>O sistema esperava <strong>{fmtL(preview.estimatedLevel)}</strong> no tanque.</div>
            {preview.variation >= 0 ? (
              <div style={{ marginTop: 4 }}>
                Isso significa que <strong>{fmtL(preview.variation)}</strong> saíram da máquina para o tanque.
              </div>
            ) : (
              <div style={{ marginTop: 4 }}>
                A leitura é <strong>{fmtL(Math.abs(preview.variation))} MENOR</strong> que o esperado.
              </div>
            )}
          </div>

          {preview.variation < 0 && (
            <Alert
              type="warning"
              showIcon
              message="Leitura menor que o esperado"
              description="O tanque não perde líquido sem descarga registrada. Isso normalmente indica erro de digitação ou descarga não apontada."
            />
          )}
          {preview.implausible && (
            <Alert
              type="warning"
              showIcon
              message="Transferência maior que o saldo da máquina"
              description={`A leitura indica que ${fmtL(preview.variation)} saíram da máquina, mas ela tem apenas ${fmtL(preview.machineBalance)} registrados hoje. Verifique o valor digitado (ou se falta apontamento de RC/processamento).`}
            />
          )}

          {suspicious && (
            <div>
              <div style={{ fontSize: 13, marginBottom: 6 }}>
                Para confirmar mesmo assim, digite novamente o valor lido no tanque:
              </div>
              <InputNumber
                value={retypeValue}
                onChange={setRetypeValue}
                min={0}
                step={0.1}
                decimalSeparator=","
                style={{ width: '100%' }}
                size="large"
                placeholder={`Redigite ${informedTank}`}
                aria-label="Redigite o valor lido no tanque para confirmar"
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );

  const mobileDeleteModal = (
    <JustificationModal
      open={!!deleteTarget}
      title="Excluir descarga"
      description={deleteTarget ? (
        <span>
          Excluir a descarga de <strong>{deleteTarget.liters} L</strong>
          {deleteTarget.machine ? <> da máquina <strong>{deleteTarget.machine.code}</strong></> : null}?
        </span>
      ) : null}
      confirmLabel="Excluir"
      danger
      loading={deleting}
      required={justRequired}
      onConfirm={handleMobileDelete}
      onCancel={() => setDeleteTarget(null)}
    />
  );

  if (mobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <BackToHome color={flowColor} />
        <Card loading={loading}>
          <ScreenHeader
            title="Registrar descarga"
            subtitle="Primeiro informe quanto tem no tanque (valor ANTES da descarga), depois o fluido e a quantidade."
            color={flowColor}
          />
          <Form form={form} layout="vertical" onFinish={handleAdd}>
            <div style={{ marginBottom: 16 }}>
              <MachineSelector value={machine} onChange={setMachine} required color={flowColor} />
            </div>
            {/* Leitura obrigatória do tanque: SEMPRE o valor ANTES da descarga.
                Mesmo padrão visual dos demais campos de litragem (QuantityStep). */}
            <QuantityStep
              form={form}
              name="tank_liters"
              label="Total no tanque agora (L)"
              hint={<span style={{ color: '#b45309', fontWeight: 600 }}>Olhe o medidor ANTES de descarregar — informe o valor de ANTES da descarga.</span>}
              autoFocus={false}
            />
            {/* PWA: mesmo padrão do seletor de máquina — na tela só o fluido
                escolhido (compacto) e as opções em modal com cards grandes.
                O desktop mantém o Select tradicional. */}
            <Form.Item label="Fluido" name="fluid_type" rules={[{ required: true, message: 'Selecione o fluido.' }]}>
              <FluidCardSelect color={flowColor} />
            </Form.Item>
            <QuantityStep
              form={form}
              name="liters"
              label="Litros"
              hint="Quantidade descarregada em litros."
            />
            <Form.Item label="Observação" name="observation">
              <Input.TextArea autoSize={{ minRows: 2 }} placeholder="Opcional" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving} size="large" block style={primaryButtonStyle(flowColor)}>
                Registrar descarga
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card loading={loading}>
          <ScreenHeader title="Descargas de hoje" subtitle="Registros do dia atual." color={flowColor} />
          {todaysList.length === 0 ? (
            <Empty description="Nenhuma descarga registrada hoje." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {todaysList.map((r) => {
                const f = EFluidType.getByCode(r.fluid_type);
                return (
                  <div
                    key={r.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 12,
                      padding: '12px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      background: '#fafbff',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {f ? <Tag>{f.description}</Tag> : <Tag>{r.fluid_type}</Tag>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong style={{ fontSize: 18 }}>{r.liters} L</strong>
                        {canCorrect && (
                          <Space size={4}>
                            <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(r)} aria-label="Corrigir descarga" />
                            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setDeleteTarget(r)} aria-label="Excluir descarga" />
                          </Space>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {r.machine ? `${r.machine.code} · ` : ''}
                      {new Date(r.occurred_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      {r.observation ? ` · ${r.observation}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {confirmModal}
        {mobileEditModal}
        {mobileDeleteModal}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card title="Registrar descarga" size="small">
        <Form form={form} layout="inline" onFinish={handleAdd}>
          <Form.Item label="Máquina" required>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Selecione a máquina"
              style={{ width: 220 }}
              value={machine?.id}
              onChange={(v) => {
                const m = machines.find((x) => x.id === v);
                setMachine(m ? { id: m.id, code: m.code, name: m.name } : null);
              }}
              options={machines.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
              aria-label="Máquina da descarga"
            />
          </Form.Item>
          <Form.Item
            label="Tanque agora (ANTES da descarga)"
            name="tank_liters"
            rules={[{ required: true, message: 'Informe o nível atual do tanque.' }]}
            tooltip="Valor que o medidor do tanque marca ANTES de descarregar."
          >
            <InputNumber min={0} step={0.1} decimalSeparator="," style={{ width: 190 }} placeholder="Litros no tanque" />
          </Form.Item>
          <Form.Item label="Fluido" name="fluid_type" rules={[{ required: true, message: 'Selecione o fluido.' }]}>
            <Select options={EFluidType.asOptions()} style={{ width: 200 }} placeholder="Selecione" />
          </Form.Item>
          <Form.Item label="Litros" name="liters" rules={[{ required: true, message: 'Informe os litros.' }]}>
            <InputNumber min={0} step={0.1} decimalSeparator="," style={{ width: 140 }} />
          </Form.Item>
          <Form.Item label="Observação" name="observation">
            <Input style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>Registrar</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="Descargas recentes" size="small"
        extra={
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Filtrar por máquina"
            style={{ width: 200 }}
            value={machineFilter}
            onChange={setMachineFilter}
            options={machines.map(m => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
          />
        }
      >
        <Table rowKey="id" size="small" loading={loading} dataSource={list} columns={columns} pagination={{ pageSize: 10 }} />
      </Card>

      {confirmModal}
    </div>
  );
};

export default DrenagemPanel;
